import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";

const LanguageContext = createContext({});

const STORAGE_KEY = "kahu_locale";
const SUPPORTED = ["tr", "en"];

/**
 * LanguageProvider — TR/EN çift dil yönetimi.
 *
 * Kaynak öncelik sırası (locale çözümü):
 * 1. URL /en/... → "en"
 * 2. localStorage KAHU_locale → "tr" / "en"
 * 3. Browser navigator.language ("en-US" → "en", "tr-TR" → "tr")
 * 4. Default "tr"
 *
 * CMS içerikleri locale'e göre çekilir. Locale değişince otomatik refetch.
 */
export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    // 1. URL kontrolü — /en prefix'i her zaman EN
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path === "/en" || path.startsWith("/en/")) return "en";
      // 2. localStorage — kullanıcının önceki bilinçli seçimi
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored && SUPPORTED.includes(stored)) return stored;
    }
    // 3. Varsayılan: Türkçe (KAHU Türkiye pazarına birincil odaklı).
    // Browser dilini varsayılan olarak kullanmıyoruz; kullanıcı EN toggle'a
    // basmadıysa TR gösterilir.
    return "tr";
  });

  const [content, setContent] = useState({});
  const [loaded, setLoaded] = useState(false);

  // Fetch content when locale changes
  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    axios.get(`${API}/content?locale=${locale}`)
      .then(res => { if (!cancelled) { setContent(res.data || {}); setLoaded(true); }})
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [locale]);

  const setLocale = useCallback((newLocale) => {
    if (!SUPPORTED.includes(newLocale)) return;
    if (newLocale === locale) return;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, newLocale);
      // URL güncelle: /en prefix ekle/çıkar
      const path = window.location.pathname;
      const search = window.location.search;
      const hash = window.location.hash;
      let newPath = path;
      if (newLocale === "en") {
        if (!path.startsWith("/en/") && path !== "/en") {
          newPath = "/en" + (path === "/" ? "" : path);
        }
      } else {
        if (path.startsWith("/en/")) newPath = path.slice(3);
        else if (path === "/en") newPath = "/";
      }
      // React Router SPA — history push yeter, page reload yok
      window.history.replaceState(null, "", newPath + search + hash);
    }
    setLocaleState(newLocale);
  }, [locale]);

  // t() helper — fallback: TR → EN → key
  const t = useCallback((key, fallback = "") => {
    return content[key] || fallback;
  }, [content]);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/content?locale=${locale}`);
      setContent(res.data || {});
    } catch {
      /* refresh silent-fail — mevcut içerik korunur */
    }
  };

  return (
    <LanguageContext.Provider value={{ locale, setLocale, content, t, loaded, refresh, isEnglish: locale === "en" }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);

/** Backward-compat alias for existing useContent() imports.
 * Hem eski useContent() hook'unu hem yeni useLanguage() hook'unu döner. */
export const useContent = () => useContext(LanguageContext);
